/** Shared, controlled multi-game selector for CPU setup and online lobbies. */
import { useRef } from 'react';
import type { GameId } from '../wasm';
import { GAME_ORDER, MULTI_PRESET_DEFS, gameIconSlug, gameLabel, gameShort } from '../lib/game-meta';
import type { MatchConfig } from '../proto';

export const MAX_SELECTED_GAMES = 8;
export const MAX_SAME_GAME = 3;
export function orderedGames(games: readonly string[]): GameId[] {
  return GAME_ORDER.filter(game => games.includes(game));
}
/** Original seven-column selector, read top to bottom then the next column. */
export const SELECTOR_ORDER = Array.from({length: 7}, (_, column) => GAME_ORDER.filter((_, index) => index % 7 === column)).flat();
export function insertGame(selected: readonly GameId[], game: GameId): GameId[] {
  if (selected.filter(g => g === game).length >= MAX_SAME_GAME || selected.length >= MAX_SELECTED_GAMES) return [...selected];
  const rank = SELECTOR_ORDER.indexOf(game);
  const before = selected.findIndex(existing => SELECTOR_ORDER.indexOf(existing) > rank);
  const next = [...selected];
  next.splice(before < 0 ? next.length : before, 0, game);
  return next;
}

export const MULTI_PRESETS = MULTI_PRESET_DEFS.filter(p => p.id === 'classic-3' || p.id === 'mixed-7').map(p => ({
  id: p.id,
  label: orderedGames(p.games).map(gameShort).join('・'),
  template: `preset:${p.id}`,
  games: orderedGames(p.games),
}));
export const SINGLE_GAMES = GAME_ORDER.map(key => ({
  key, iconSlug: gameIconSlug(key), label: gameLabel(key),
  template: `preset:${key === 'AnimalShogi' ? 'animal' : gameIconSlug(key)}`,
}));

/** Only user edits normalize the selection; existing saved configs are never rewritten on mount. */
export function withSelectedGames(config: MatchConfig, games: readonly string[]): MatchConfig {
  const counts = new Map<string, number>();
  const ordered = games.filter((g): g is GameId => {
    const count = counts.get(g) ?? 0;
    if (!GAME_ORDER.includes(g as GameId) || count >= MAX_SAME_GAME) return false;
    counts.set(g, count + 1); return true;
  }).slice(0, MAX_SELECTED_GAMES);
  const preset = MULTI_PRESETS.find(p => p.games.length === ordered.length && p.games.every((g, i) => g === ordered[i]));
  const single = ordered.length === 1 ? SINGLE_GAMES.find(g => g.key === ordered[0]) : null;
  return { ...config, games: ordered, template: preset?.template ?? single?.template ?? 'custom' };
}

export function GamePicker({ config, isHost = true, showLabel = true, onChange }: {
  config: MatchConfig; isHost?: boolean; showLabel?: boolean; onChange: (next: MatchConfig) => void;
}) {
  const selected = (config.games ?? []).filter((g): g is GameId => GAME_ORDER.includes(g as GameId));
  const nextId = useRef(0);
  const slots = useRef<{ id: string; game: GameId }[]>([]);
  if (slots.current.length !== selected.length || slots.current.some((slot, i) => slot.game !== selected[i])) {
    const available = [...slots.current];
    slots.current = selected.map(game => {
      const found = available.findIndex(slot => slot.game === game);
      return found < 0 ? {id: `selection-${nextId.current++}`, game} : available.splice(found, 1)[0];
    });
  }
  const changeSlots = (next: typeof slots.current) => {
    slots.current = next;
    onChange(withSelectedGames(config, next.map(slot => slot.game)));
  };
  const move = (index: number, offset: number) => {
    const target = index + offset;
    if (target < 0 || target >= selected.length) return;
    const next = [...slots.current];
    const [slot] = next.splice(index, 1);
    next.splice(target, 0, slot);
    changeSlots(next);
  };

  const drag = useRef<{ id: string; x: number; y: number; moved: boolean } | null>(null);
  const suppressClick = useRef(false);

  const currentPreset = MULTI_PRESETS.find(p => p.games.length === selected.length && p.games.every((g, i) => g === selected[i]));

  return <div className="time-control game-picker">
    {showLabel && <div className="time-control-label"><span className="time-control-eyebrow">対局構成</span>{!isHost && <small className="muted">ホストのみ変更可</small>}</div>}
    <div className="time-control-options">
      {MULTI_PRESETS.map(p => <button type="button" key={p.id} className={`time-control-option ${currentPreset?.id === p.id ? 'active' : ''}`} disabled={!isHost} aria-pressed={currentPreset?.id === p.id} data-testid={`preset-${p.id}`} onClick={() => onChange(withSelectedGames(config, p.games))}>{p.label}</button>)}
      <button type="button" className={`time-control-option ${currentPreset ? '' : 'active'}`} disabled={!isHost} aria-pressed={!currentPreset} data-testid="preset-custom">カスタム</button>
      {!currentPreset && <small className="game-picker-custom-names">{selected.map(gameShort).join('・')}</small>}
    </div>
    <span className="sr-only" id="game-picker-order-help">ドラッグか左右キーで並べ替え。押すとゲームを外します。</span>
    <div className="game-picker-surface">
    <div className="game-picker-list game-picker-selected" role="group" aria-label="現在のゲーム"
      onPointerMove={event => {
        const current = drag.current;
        if (!current) return;
        if (!current.moved && Math.hypot(event.clientX - current.x, event.clientY - current.y) < 8) return;
        current.moved = true;
        suppressClick.current = true;
        // Capture on the stable list: moving a slot can detach its DOM node.
        event.currentTarget.setPointerCapture(event.pointerId);
        const target = document.elementFromPoint(event.clientX, event.clientY)?.closest<HTMLElement>('[data-selected-slot]')?.dataset.selectedSlot;
        const from = slots.current.findIndex(slot => slot.id === current.id);
        const to = slots.current.findIndex(slot => slot.id === target);
        if (from >= 0 && to >= 0 && from !== to) move(from, to - from);
      }}
      onPointerUp={() => { drag.current = null; }}
      onPointerCancel={() => { drag.current = null; suppressClick.current = false; }}>

      {slots.current.map(({game, id}, index) => <div className="game-picker-selected-item" data-selected-game={game} data-selected-slot={id} key={id}>
        <button type="button" className="game-picker-item" disabled={!isHost}
          aria-label={`${gameLabel(game)}を外す`} aria-describedby="game-picker-order-help"
          title={gameLabel(game)} data-testid={`selected-${gameIconSlug(game)}`}
          onKeyDown={event => {
            if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
              event.preventDefault();
              move(index, event.key === 'ArrowLeft' ? -1 : 1);
            }
          }}
          onPointerDown={event => {
            if (event.button !== 0) return;
            suppressClick.current = false;
            drag.current = { id, x: event.clientX, y: event.clientY, moved: false };
            event.currentTarget.setPointerCapture(event.pointerId);
          }}
          onClick={event => {
            if (suppressClick.current && event.detail > 0) { suppressClick.current = false; return; }
            changeSlots(slots.current.filter(slot => slot.id !== id));
          }}>
          <img src={`/icons/games/${gameIconSlug(game)}.svg`} width={36} height={36} draggable={false} alt=""/><span className="game-picker-icon-label">{gameLabel(game)}</span>
        </button>
      </div>)}
    </div>
    </div>
    <div className="game-picker-grid game-picker-available" role="group" aria-label="マルチゲームセレクタ">
      {SINGLE_GAMES.map(g => <button type="button" key={g.key} className={`game-picker-icon game-picker-icon--${selected.includes(g.key) ? 'on' : 'off'}`} disabled={!isHost || selected.filter(game => game === g.key).length >= MAX_SAME_GAME || selected.length >= MAX_SELECTED_GAMES} aria-pressed={selected.includes(g.key)} aria-label={`${g.label}を追加${selected.filter(game => game === g.key).length >= MAX_SAME_GAME ? '（3盤まで）' : ''}`} title={g.label} data-testid={`game-${g.iconSlug}`} onClick={() => { if (selected.length < MAX_SELECTED_GAMES) onChange(withSelectedGames(config, insertGame(selected, g.key))); }}><img src={`/icons/games/${g.iconSlug}.svg`} width={44} height={44} alt=""/><span className="game-picker-icon-label">{g.label}</span></button>)}
    </div>
  </div>;
}
